import {
    QueryClient,
    type QueryKey,
    type UseBaseQueryOptions,
    type UseQueryResult,
    type UseSuspenseQueryResult,
    useQuery,
    useSuspenseQuery
} from "@tanstack/react-query"
import { AxiosError } from "axios"
import isUndefined from "lodash-es/isUndefined"

import { coachInfoAdapter } from "@/3514/adapters"
import { ECoachInfoQueryKey } from "@/3514/config"
import { coachInfoFetcher } from "@/3514/fetchers"
import type { ICoachInfoDto, ICoachInfoModel } from "@/3514/types"
import { hours, minutes, seconds } from "@/3514/utils/time.utils"

type _TCoachInfoQueryOptions = Pick<UseBaseQueryOptions, "staleTime" | "gcTime" | "enabled"> & { participantId: number }

type TCoachInfoSuspenseQuery = UseSuspenseQueryResult<ICoachInfoModel, AxiosError>
type TCoachInfoQuery = UseQueryResult<ICoachInfoModel, AxiosError>

function useCoachInfoSuspenseQuery(options: _TCoachInfoQueryOptions): TCoachInfoSuspenseQuery {
    const _defaultOptions: Partial<_TCoachInfoQueryOptions> = { staleTime: undefined, gcTime: undefined }

    const actualOptions: _TCoachInfoQueryOptions = { ..._defaultOptions, ...options }

    return useSuspenseQuery<ICoachInfoDto, AxiosError, ICoachInfoModel, QueryKey>({
        queryKey: ECoachInfoQueryKey({ participantId: actualOptions.participantId }),
        queryFn: coachInfoFetcher,
        select: coachInfoAdapter,
        staleTime: isUndefined(actualOptions.staleTime) ? hours(1) : actualOptions.staleTime,
        gcTime: isUndefined(actualOptions.gcTime) ? minutes(20) : actualOptions.gcTime,
        retry: 1,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data"]
    })
}

function useCoachInfoQuery(options: _TCoachInfoQueryOptions): TCoachInfoQuery {
    const _defaultOptions: Partial<_TCoachInfoQueryOptions> = {
        staleTime: undefined,
        gcTime: undefined,
        enabled: false
    }

    const actualOptions: _TCoachInfoQueryOptions = { ..._defaultOptions, ...options }

    return useQuery<ICoachInfoDto, AxiosError, ICoachInfoModel, QueryKey>({
        queryKey: ECoachInfoQueryKey({ participantId: actualOptions.participantId }),
        queryFn: coachInfoFetcher,
        select: coachInfoAdapter,
        staleTime: isUndefined(actualOptions.staleTime) ? hours(1) : actualOptions.staleTime,
        gcTime: isUndefined(actualOptions.gcTime) ? minutes(20) : actualOptions.gcTime,
        retry: 1,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "isFetching", "isFetched"],
        enabled: actualOptions.enabled
    })
}

async function coachInfoQueryPrefetcher({ qc }: { qc: QueryClient }, { userId }: { userId: number }): Promise<void> {
    await qc.prefetchQuery<ICoachInfoDto, AxiosError, ICoachInfoModel, QueryKey>({
        queryKey: ECoachInfoQueryKey({ participantId: userId }),
        queryFn: coachInfoFetcher,
        staleTime: hours(1),
        gcTime: minutes(20)
    })
}

export { useCoachInfoSuspenseQuery, useCoachInfoQuery, coachInfoQueryPrefetcher }
export type { TCoachInfoSuspenseQuery, TCoachInfoQuery }
